import { memo, useRef, useEffect } from 'react';
import clsx from 'clsx';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  UserIcon,
  ClockIcon,
  EyeSlashIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const VIOLATION_LABELS = {
  MISSING_VEST: 'Missing Vest',
  MISSING_HELMET: 'Missing Helmet',
  MISSING_GLOVES: 'Missing Gloves',
  MISSING_GOGGLES: 'Missing Goggles',
  MISSING_BOOTS: 'Missing Boots',
  MISSING_MASK: 'Missing Mask',
  MISSING_HARNESS: 'Missing Harness',
  FALL_DETECTED: 'Fall Detected',
};

const ConfidenceBar = ({ value }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div
        className={clsx(
          'h-full rounded-full transition-all duration-300',
          value >= 0.8 ? 'bg-green-500' : value >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
        )}
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
    <span className="text-xs text-slate-500 w-10 text-right">{(value * 100).toFixed(0)}%</span>
  </div>
);

const PersonCard = memo(({ person, isHighlighted }) => (
  <div
    className={clsx(
      'p-3 rounded-lg border transition-all duration-300',
      person.compliant
        ? 'bg-green-50 border-green-200'
        : 'bg-red-50 border-red-300',
      isHighlighted && 'ring-2 ring-blue-400'
    )}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <span className={clsx(
          'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold',
          person.compliant ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        )}>
          {person.trackId}
        </span>
        <span className="text-sm font-medium text-slate-700">Track #{person.trackId}</span>
      </div>
      {person.compliant ? (
        <ShieldCheckIcon className="w-5 h-5 text-green-600" />
      ) : (
        <ShieldExclamationIcon className="w-5 h-5 text-red-600" />
      )}
    </div>

    <ConfidenceBar value={person.confidence} />

    {person.violations.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1">
        {person.violations.map((v) => (
          <span
            key={v}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200"
          >
            {VIOLATION_LABELS[v] || v}
          </span>
        ))}
      </div>
    )}

    {person.zone && (
      <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
        <MapPinIcon className="w-3.5 h-3.5" />
        {person.zone}
      </div>
    )}
  </div>
));

PersonCard.displayName = 'PersonCard';

const DetectionPanel = ({ detections, className }) => {
  const scrollRef = useRef(null);
  const prevViolationCountRef = useRef(0);

  // Auto-scroll to violations when count changes
  useEffect(() => {
    if (!detections || !scrollRef.current) return;
    if (detections.violationCount > prevViolationCountRef.current) {
      const firstViolation = scrollRef.current.querySelector('[data-violation="true"]');
      if (firstViolation) {
        firstViolation.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
    prevViolationCountRef.current = detections.violationCount;
  }, [detections?.violationCount]);

  if (!detections) {
    return (
      <div className={clsx('flex flex-col items-center justify-center p-6 text-slate-400', className)}>
        <EyeSlashIcon className="w-8 h-8 mb-2" />
        <p className="text-sm">Waiting for detection data...</p>
      </div>
    );
  }

  const { persons = [], totalPersons = 0, compliantCount = 0, violationCount = 0, processingTimeMs = 0 } = detections;

  // Sort: violations first
  const sortedPersons = [...persons].sort((a, b) => {
    if (a.compliant === b.compliant) return a.trackId - b.trackId;
    return a.compliant ? 1 : -1;
  });

  return (
    <div className={clsx('flex flex-col h-full bg-white', className)}>
      {/* Summary bar */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 bg-slate-50">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2 text-sm">
            <UserIcon className="w-4 h-4 text-slate-500" />
            <span className="font-medium">{totalPersons}</span>
            <span className="text-slate-500">persons</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheckIcon className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-700">{compliantCount}</span>
            <span className="text-slate-500">compliant</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldExclamationIcon className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-700">{violationCount}</span>
            <span className="text-slate-500">violations</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ClockIcon className="w-4 h-4 text-slate-500" />
            <span className="font-medium">{processingTimeMs.toFixed(0)}</span>
            <span className="text-slate-500">ms</span>
          </div>
        </div>
      </div>

      {/* Person cards */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {sortedPersons.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No persons detected</p>
        ) : (
          sortedPersons.map((person) => (
            <div key={person.trackId} data-violation={!person.compliant || undefined}>
              <PersonCard person={person} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default memo(DetectionPanel);
